import api from "../../../../lib/api";
import { Input } from "../../../../components/ui/input";
import { toast } from "sonner";

/**
 * CarrierCredentialDialog — modal that collects OAuth or API-key style
 * credentials to wire a BYO carrier account (UPS/FedEx/USPS/DHL) into
 * the seller's account. Extracted from SellerDropship.jsx in Phase 3.
 */
export default function CarrierCredentialDialog({
  carrierDialog, setCarrierDialog, setCarrierAccounts,
}) {
  if (!carrierDialog) return null;
  return (
  <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6" onClick={() => setCarrierDialog(null)}>
    <div className="bg-white rounded-2xl max-w-lg w-full p-6 space-y-4" onClick={e => e.stopPropagation()} data-testid="carrier-credential-dialog">
      <div>
        <div className="text-[15px] font-semibold">Connect {carrierDialog.meta?.label || carrierDialog.carrier}</div>
        <div className="text-[12px] text-foreground/60 mt-1">{carrierDialog.meta?.help || ""}</div>
      </div>
      {/* Phase 4i · OAuth path (UPS/FedEx/DHL/USPS only) */}
      {["ups","fedex","dhl_express","usps"].includes(carrierDialog.carrier) && (
        <div className="rounded-xl border border-indigo-200 bg-indigo-50/40 p-3 flex items-center justify-between gap-3">
          <div>
            <div className="text-[12.5px] font-medium">Connect with OAuth (recommended)</div>
            <div className="text-[11px] text-foreground/55 mt-0.5">
              Sign in on {carrierDialog.meta?.label}&apos;s site — no credentials leave this tab.
            </div>
          </div>
          <button
            onClick={async () => {
              try {
                const { data } = await api.get(`/dropship/carrier-oauth/start?carrier=${carrierDialog.carrier}`);
                window.open(data.authorize_url, "_blank", "width=520,height=680");
                setCarrierDialog(null);
              } catch { toast.error("OAuth unavailable — use manual form"); }
            }}
            data-testid="oauth-start"
            className="text-[11.5px] mono uppercase tracking-[0.14em] rounded-full h-9 px-4 bg-indigo-600 text-white hover:bg-indigo-700">
            OAuth
          </button>
        </div>
      )}

      <Input placeholder="Nickname (e.g., 'My primary UPS')"
        value={carrierDialog.nickname || ""}
        onChange={e => setCarrierDialog(d => ({ ...d, nickname: e.target.value }))}
        className="h-10"/>
      {(carrierDialog.meta?.parameters || []).map(p => (
        <div key={p}>
          <label className="text-[10.5px] mono uppercase tracking-[0.16em] text-foreground/55 block mb-1">{p.replace(/_/g, " ")}</label>
          <Input
            type={p.toLowerCase().includes("password") ? "password" : "text"}
            value={carrierDialog.params[p] || ""}
            onChange={e => setCarrierDialog(d => ({ ...d, params: { ...d.params, [p]: e.target.value } }))}
            className="h-10 mono"
            data-testid={`carrier-param-${p}`}
          />
        </div>
      ))}
      <div className="flex items-center justify-end gap-2 pt-2">
        <button onClick={() => setCarrierDialog(null)}
          className="text-[11.5px] mono uppercase tracking-[0.14em] rounded-full h-9 px-4 border border-border hover:bg-secondary">
          Cancel
        </button>
        <button
          onClick={async () => {
            try {
              const { data } = await api.post("/dropship/carrier-accounts", {
                carrier:  carrierDialog.carrier,
                nickname: carrierDialog.nickname || carrierDialog.meta?.label,
                parameters: carrierDialog.params,
              });
              setCarrierAccounts(list => [{ ...data }, ...list]);
              setCarrierDialog(null);
              toast.success(`${carrierDialog.meta?.label || carrierDialog.carrier} connected`);
            } catch (e) {
              const detail = e.response?.data?.detail;
              const msg = typeof detail === "string"
                ? detail
                : detail?.message || "Could not connect carrier";
              toast.error(msg);
            }
          }}
          data-testid="carrier-credential-submit"
          className="text-[11.5px] mono uppercase tracking-[0.14em] rounded-full h-9 px-4 bg-foreground text-background hover:bg-foreground/90">
          Connect
        </button>
      </div>
    </div>
  </div>
  );
}
